import React, { useState } from "react";
import { Link } from "react-router-dom";
import CommentItem from "../map/CommentItem";
import PhotoCarousel from "../map/PhotoCarousel";
import SpotMiniMap from "../map/SpotMiniMap";
import PhotoUploadPreview from "../PhotoUploadPreview";
import { PHOTO_UPLOAD_ACCEPT } from "../../utils/uploadValidation";
import { placeTypeRu } from "../../utils/placeTypeRu";
import { fishingTypeSelectOptions } from "../../utils/fishLabelsRu";
import OptionMultiSelect from "../OptionMultiSelect";
import { withStaticAssetVersion } from "../../utils/staticAssetVersion";
import {
    SpotBoolIcon,
    WEATHER_ICON_FILES,
    commentFieldError,
    fishingMethodLabel,
    formatCloudinessText,
    formatPrecipitationText,
    formatWindDirectionShort,
    getMoonPhaseEmoji,
    getWindArrowRotation,
    pickWeatherIconKey,
    toKmh,
} from "../../utils/spotDetailUtils";

export default function SpotDetailView({
    detail: detailProp,
    variant = "page",
    onClose,
    onDrawRoute,
    showNotes = false,
    showVideos = false,
    tabsMenuOpen: _tabsMenuOpen,
    setTabsMenuOpen: _setTabsMenuOpen,
    onTabChange: _onTabChange,
    onPhotoTabChange,
    onWeatherSubtabChange,
    commentsMountHidden = false,
    commentsLoading = false,
    extraMainActions,
    panelClassName = "spot-page__panel",
    ...rest
}) {
    const {
        spot,
        message,
        isAuthenticated,
        visibleComments,
        selectedFish,
        forecast,
        forecastLoading,
        forecastError,
        selectedForecastSlot,
        setSelectedForecastSlot,
        forecastTable,
        loadForecast,
        nearestWeatherLoading,
        nearestWeatherError,
        weatherTimelineLoading,
        weatherTimelineError,
        weatherTimelineFetchedAt,
        weatherForecastTable,
        weatherSource,
        weatherIconKey,
        precipitationValue,
        cloudinessValue,
        commentText,
        setCommentText,
        commentForm,
        commentFiles,
        setCommentFiles,
        commentSubmitting,
        commentError,
        commentFieldErrors,
        commentFileErrors,
        setCommentFieldErrors,
        setCommentError,
        setCommentFileErrors,
        commentFormOpen,
        setCommentFormOpen,
        maxFishingDateTime,
        setCommentFormField,
        commentInputClass,
        handleCommentPhotoChange,
        submitComment,
        actionMessage,
        liked,
        favorited,
        likePending,
        favoritePending,
        toggleLike,
        toggleFavorite,
        openReportModal,
        routes,
        selectedRouteIndex,
        setSelectedRouteIndex,
        mapRoutes,
        videos,
        videoUrl,
        setVideoUrl,
        videoTitle,
        setVideoTitle,
        addVideo,
        notes,
        noteFormOpen,
        setNoteFormOpen,
        noteText,
        setNoteText,
        notePendingId,
        noteError,
        createNote,
        deleteNote,
        activeTab,
        photoType,
        setPhotoType,
        weatherType,
        setWeatherType,
        showAllFish,
        setShowAllFish,
        mapTitle,
        openLoginModalForComments,
        fish,
        photoSource,
        heroImage,
        depthLabel,
        activeBans,
    } = detailProp || rest;

    const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
    const [weatherMenuOpen, setWeatherMenuOpen] = useState(false);

    if (!spot) return null;

    const heroClass = variant === "page" ? "spot-page__hero" : "entity-popup__hero";
    const commentGridClass = variant === "page" ? "page-grid" : "comments-page-grid";
    const photosEmptyMessage = "Фото отсутствуют. Вы можете добавить фото в отчёт и они отобразятся в общей галерее";

    const photoTabOptions = [
        { id: "place", label: "Место" },
        { id: "catch", label: "Улов" },
        { id: "deep", label: "Эхолот" },
    ];
    const activePhotoTabLabel = photoTabOptions.find((item) => item.id === photoType)?.label || "Место";

    const weatherTabOptions = [
        { id: "bite", label: "Прогноз клёва" },
        { id: "weather", label: "Погода" },
    ];
    const activeWeatherTabLabel = weatherTabOptions.find((item) => item.id === weatherType)?.label || "Прогноз клёва";

    const handlePhotoTypeChange = (type) => {
        setPhotoType(type);
        setPhotoMenuOpen(false);
        onPhotoTabChange?.(type);
    };

    const handleWeatherTypeChange = (type) => {
        setWeatherType(type);
        setWeatherMenuOpen(false);
        onWeatherSubtabChange?.(type);
    };

    const renderWeatherSubtabs = () => (
        <div className="spot-weather-tabs-row">
            <nav
                className={`profile-glass-menu spot-page__glass-menu spot-weather-tabs__glass-menu${weatherMenuOpen ? " is-open" : ""}`}
                aria-label="Прогноз и погода"
            >
                <button
                    type="button"
                    className="profile-glass-menu__summary"
                    onClick={() => setWeatherMenuOpen((value) => !value)}
                    aria-expanded={weatherMenuOpen}
                >
                    <span>{activeWeatherTabLabel}</span>
                    <svg aria-hidden className="profile-glass-menu__chevron" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <div className="profile-glass-menu__body">
                    {weatherTabOptions.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={`profile-glass-menu__link profile-glass-menu__button${weatherType === item.id ? " is-active" : ""}`}
                            onClick={() => handleWeatherTypeChange(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </nav>
            <div className="spot-page__tabs spot-weather-tabs spot-page__tabs--desktop" role="tablist" aria-label="Прогноз и погода">
                <button type="button" role="tab" aria-selected={weatherType === "bite"} className={`spot-page__tab${weatherType === "bite" ? " is-active" : ""}`} onClick={() => handleWeatherTypeChange("bite")}>Прогноз клёва</button>
                <button type="button" role="tab" aria-selected={weatherType === "weather"} className={`spot-page__tab${weatherType === "weather" ? " is-active" : ""}`} onClick={() => handleWeatherTypeChange("weather")}>Погода</button>
            </div>
        </div>
    );

    const handleFishLinkClick = variant === "popup" ? onClose : undefined;

    const renderEngagementBar = () => (
        <div className="spot-page__engagement-bar">
            {!isAuthenticated && (
                <>
                    <span className="article-item-muted">
                        <span aria-hidden style={{ opacity: 0.45 }}>♥</span>
                        <span>{spot.likesCount ?? 0}</span>
                    </span>
                    <span className="article-item-muted">
                        <span aria-hidden style={{ opacity: 0.45 }}>★</span>
                        <span>{spot.favoritesCount ?? 0}</span>
                    </span>
                </>
            )}
            {isAuthenticated && (
                <>
                    <button
                        type="button"
                        className={liked ? "btn-liked" : "btn-like"}
                        onClick={toggleLike}
                        disabled={likePending}
                        aria-label={liked ? "Убрать лайк" : "Поставить лайк"}
                    >
                        <span aria-hidden style={{ opacity: liked ? 1 : 0.45 }}>♥</span>
                        <span>{spot.likesCount ?? 0}</span>
                    </button>
                    <button
                        type="button"
                        className={favorited ? "btn-liked" : "btn-like"}
                        onClick={toggleFavorite}
                        disabled={favoritePending}
                        aria-label={favorited ? "Убрать из избранного" : "В избранное"}
                    >
                        <span aria-hidden style={{ opacity: favorited ? 1 : 0.45 }}>★</span>
                        <span>{spot.favoritesCount ?? 0}</span>
                    </button>
                </>
            )}
            {variant === "popup" && extraMainActions}
            {isAuthenticated && (
                <button type="button" className="btn-secondary spot-page__engagement-report" onClick={openReportModal}>
                    Пожаловаться
                </button>
            )}
        </div>
    );

    const renderMainTab = () => (
        <div className={`${panelClassName} spot-main-tab-panel`}>
            {!!heroImage && <img className={heroClass} src={heroImage} alt={mapTitle} />}
            <table className="spot-info">
                <thead>
                    <tr className="spot-info-header">
                        <td colSpan={2}>Общая информация</td>
                    </tr>
                </thead>
                <tbody>
                    <tr className="spot-item">
                        <td>Тип</td>
                        <td className="spot-item-bold">{placeTypeRu(spot.place?.type)}</td>
                    </tr>
                    <tr className="spot-item">
                        <td>Тип берега</td>
                        <td className="spot-item-bold">{spot.bankType || "-"}</td>
                    </tr>
                    <tr className="spot-item">
                        <td>Глубина (максимальная)</td>
                        <td className="spot-item-bold">{depthLabel}</td>
                    </tr>
                    <tr className="spot-item">
                        <td>Течение</td>
                        <td className="spot-item-bold">{spot.currentStrength || "Отсутствует"}</td>
                    </tr>
                    <tr className="spot-item">
                        <td>Метод ловли</td>
                        <td className="spot-item-bold">{fishingMethodLabel(spot)}</td>
                    </tr>
                    <tr className="spot-item spot-item--fish">
                        <td>Рыба</td>
                        <td>
                            {!fish.length && <span className="spot-item-bold">-</span>}
                            {!!fish.length && (
                                <div className="spot-fish-list">
                                    {(showAllFish ? fish : fish.slice(0, 5)).map((item) => (
                                        <Link
                                            className="badge badge--muted spot-info-fish-item"
                                            key={item.id}
                                            to={`/fishes/${item.slug || item.id}`}
                                            onClick={handleFishLinkClick}
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                    {fish.length > 5 && !showAllFish && (
                                        <button
                                            type="button"
                                            className="badge badge--muted spot-info-fish-item"
                                            style={{ border: "none" }}
                                            onClick={() => setShowAllFish(true)}
                                        >
                                            +{fish.length - 5}
                                        </button>
                                    )}
                                </div>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            <table className="spot-info">
                <thead>
                    <tr className="spot-info-header">
                        <td colSpan={2}>Дополнительная информация</td>
                    </tr>
                </thead>
                <tbody>
                    <tr className="spot-item">
                        <td>Парковка</td>
                        <td className="spot-item-bold"><SpotBoolIcon value={spot.hasParking} /></td>
                    </tr>
                    <tr className="spot-item">
                        <td>Место для палатки</td>
                        <td className="spot-item-bold"><SpotBoolIcon value={spot.tentAllowed} /></td>
                    </tr>
                    <tr className="spot-item">
                        <td>Возможность ночной ловли</td>
                        <td className="spot-item-bold"><SpotBoolIcon value={spot.nightFishingAllowed} /></td>
                    </tr>
                    <tr className="spot-item">
                        <td>Можно с детьми</td>
                        <td className="spot-item-bold"><SpotBoolIcon value={spot.suitableForChildren} /></td>
                    </tr>
                </tbody>
            </table>
            <table className="spot-info">
                <thead>
                    <tr className="spot-info-header">
                        <td colSpan={2}>Особая информация</td>
                    </tr>
                </thead>
                <tbody>
                    {spot.isSpawningArea && (
                        <tr className="spot-item">
                            <td>Нерестовая зона ⚠️</td>
                            <td className="spot-item-bold">Уточняйте местные правила.</td>
                        </tr>
                    )}
                    {spot.isWinterHole && (
                        <tr className="spot-item">
                            <td>Зимовальная яма ⚠️</td>
                            <td className="spot-item-bold">Рыбалка может быть ограничена.</td>
                        </tr>
                    )}
                    {activeBans.length > 0
                        ? activeBans.map((ban) => (
                            <tr key={ban.id || `${ban.fishName}-${ban.startDate}`} className="spot-item">
                                <td>Запрет по рыбе {ban.fishName || "вид"} 🔴</td>
                                <td className="spot-item-bold">{ban.startDate} - {ban.endDate}</td>
                            </tr>
                        ))
                        : (
                            <tr className="spot-item">
                                <td>Запретов нет ✅</td>
                                <td className="spot-item-bold">Для этой точки активных ограничений нет.</td>
                            </tr>
                        )}
                </tbody>
            </table>
            <table className="spot-info">
                <thead>
                    <tr className="spot-info-header">
                        <td colSpan={2}>Описание</td>
                    </tr>
                </thead>
                <tbody>
                    <tr className="spot-item spot-item--description">
                        <td colSpan={2}>{spot.description || "-"}</td>
                    </tr>
                </tbody>
            </table>
            {renderEngagementBar()}
            {!!actionMessage && <p className="entity-popup__muted">{actionMessage}</p>}
        </div>
    );

    const renderNotesTab = () => (
        <div className={panelClassName}>
            <section className="spot-page__section spot-page__notes">
                <div className="spot-page__notes-head">
                    <h4>Заметки (видны только мне)</h4>
                    {!noteFormOpen && (
                        <button type="button" className="btn-primary" onClick={() => setNoteFormOpen(true)}>
                            Добавить заметку
                        </button>
                    )}
                </div>
                {noteFormOpen && (
                    <div className="spot-page__note-form">
                        <textarea className="ui-textarea" rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                        <div className="spot-page__note-form-actions">
                            <button type="button" className="toolbar-btn" onClick={createNote} disabled={!noteText.trim()}>
                                Сохранить заметку
                            </button>
                            <button
                                type="button"
                                className="btn-outline"
                                onClick={() => {
                                    setNoteFormOpen(false);
                                    setNoteText("");
                                }}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}
                {!!noteError && <p className="form-message">{noteError}</p>}
                {notes.map((note) => (
                    <div key={note.id} className="page-item spot-page__note-item">
                        <div className="spot-page__note-top">
                            <p className="spot-page__note-text">{note.text}</p>
                            <div className="spot-page__note-actions">
                                <button
                                    type="button"
                                    className="spot-note-icon-btn spot-note-icon-btn--danger"
                                    onClick={() => deleteNote(note.id)}
                                    disabled={notePendingId === note.id}
                                    aria-label="Удалить заметку"
                                    title="Удалить заметку"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );

    const renderPhotoTabs = () => (
        <>
            <nav
                className={`profile-glass-menu spot-page__glass-menu spot-photo-tabs__glass-menu${photoMenuOpen ? " is-open" : ""}`}
                aria-label="Тип фото"
            >
                    <button
                        type="button"
                        className="profile-glass-menu__summary"
                        onClick={() => setPhotoMenuOpen((value) => !value)}
                        aria-expanded={photoMenuOpen}
                    >
                        <span>{activePhotoTabLabel}</span>
                        <svg aria-hidden className="profile-glass-menu__chevron" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <div className="profile-glass-menu__body">
                        {photoTabOptions.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`profile-glass-menu__link profile-glass-menu__button${photoType === item.id ? " is-active" : ""}`}
                                onClick={() => handlePhotoTypeChange(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </nav>
            <div className="spot-page__tabs spot-photo-tabs spot-page__tabs--desktop" role="tablist" aria-label="Тип фото">
                <button type="button" role="tab" aria-selected={photoType === "place"} className={`spot-page__tab${photoType === "place" ? " is-active" : ""}`} onClick={() => handlePhotoTypeChange("place")}>Место</button>
                <button type="button" role="tab" aria-selected={photoType === "catch"} className={`spot-page__tab${photoType === "catch" ? " is-active" : ""}`} onClick={() => handlePhotoTypeChange("catch")}>Улов</button>
                <button type="button" role="tab" aria-selected={photoType === "deep"} className={`spot-page__tab${photoType === "deep" ? " is-active" : ""}`} onClick={() => handlePhotoTypeChange("deep")}>Эхолот</button>
            </div>
        </>
    );

    const renderPhotosTab = () => (
        <div className={`${panelClassName} spot-photo-panel`}>
            <div className="spot-photo-tabs-row">
                {renderPhotoTabs()}
            </div>
            {photoSource.length > 0 ? (
                <PhotoCarousel photos={photoSource} stateKey={`${spot?.id ?? "spot"}:${photoType}`} />
            ) : (
                <p className="spot-photo-empty">{photosEmptyMessage}</p>
            )}
            {showVideos && videos.length > 0 && (
                <section className="spot-page__section">
                    <h4>Видео YouTube</h4>
                    {isAuthenticated && (
                        <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
                            <input className="ui-input" placeholder="Ссылка на ролик YouTube" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
                            <input className="ui-input" placeholder="Заголовок (необязательно)" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} />
                            <button type="button" className="toolbar-btn" onClick={addVideo}>Добавить видео</button>
                        </div>
                    )}
                    {videos.map((video) => (
                        <iframe
                            key={video.id}
                            className="spot-page__video"
                            title={video.title || video.youtubeVideoId}
                            src={`https://www.youtube.com/embed/${video.youtubeVideoId}`}
                            allowFullScreen
                        />
                    ))}
                </section>
            )}
        </div>
    );

    const renderWeatherTab = () => (
        <div className={`${panelClassName} spot-weather-tab-panel`}>
            {renderWeatherSubtabs()}
            {weatherType === "bite" && (
                <>
                    {nearestWeatherLoading && <p className="page-status-message">Загрузка погоды...</p>}
                    {!!nearestWeatherError && <p className="form-message">{nearestWeatherError}</p>}
                    <table className="spot-info">
                        <thead>
                            <tr className="spot-info-header">
                                <td colSpan={2}>Погода сейчас</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="spot-item spot-item--weather-now">
                                <td>Температура</td>
                                <td>
                                    <div className="entity-popup-weather__data" style={{ minHeight: "60px", display: "flex", alignItems: "center" }}>
                                        <img
                                            className="entity-popup-weather__icon"
                                            src={withStaticAssetVersion(`${process.env.PUBLIC_URL || ""}/weather-icons/${WEATHER_ICON_FILES[weatherIconKey]}`)}
                                            alt=""
                                            width={38}
                                            height={38}
                                        />
                                        <span className="entity-popup-weather__value">
                                            {weatherSource.temperatureAir ?? weatherSource.tempAir ?? "-"}°
                                        </span>
                                        <span className="entity-popup-weather__hint">
                                            {formatPrecipitationText(precipitationValue)}, {formatCloudinessText(cloudinessValue)}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                            <tr className="spot-item spot-item--weather-now">
                                <td>Давление</td>
                                <td>
                                    <div className="entity-popup-weather__data" style={{ minHeight: "60px", display: "flex", alignItems: "center" }}>
                                        <span role="img" aria-label="Давление">📈</span>
                                        <span>{weatherSource.pressure ?? weatherSource.pressureMm ?? "-"} мм</span>
                                    </div>
                                </td>
                            </tr>
                            <tr className="spot-item spot-item--weather-now">
                                <td>Ветер</td>
                                <td>
                                    <div className="entity-popup-weather__data" style={{ minHeight: "60px", display: "flex", alignItems: "center" }}>
                                        <span
                                            role="img"
                                            aria-label="Ветер"
                                            style={{ display: "inline-block", transform: `rotate(${getWindArrowRotation(weatherSource.windDirection)}deg)` }}
                                        >
                                            ↑
                                        </span>
                                        <span>{formatWindDirectionShort(weatherSource.windDirection)} {toKmh(weatherSource.windSpeed)} км/ч</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <table className="spot-info">
                        <thead>
                            <tr className="spot-info-header">
                                <td colSpan={2}>Прогноз</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="spot-item">
                                <td>Рыба</td>
                                <td className="spot-item-bold">
                                    {!fish.length && <p className="entity-popup__muted">Для точки пока не указаны виды рыб.</p>}
                                    {!!fish.length && (
                                        <select
                                            className="ui-select spot-bite-fish-select"
                                            value={selectedFish?.id || fish[0]?.id || ""}
                                            onChange={(e) => {
                                                const fishItem = fish.find((item) => String(item.id) === e.target.value);
                                                if (fishItem) loadForecast(fishItem);
                                            }}
                                        >
                                            {fish.map((item) => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </td>
                            </tr>
                            {forecastLoading && (
                                <tr className="spot-item">
                                    <td>Статус</td>
                                    <td className="spot-item-bold">Загрузка прогноза...</td>
                                </tr>
                            )}
                            {!!forecastError && (
                                <tr className="spot-item">
                                    <td>Статус</td>
                                    <td className="spot-item-bold"><p className="form-message">{forecastError}</p></td>
                                </tr>
                            )}
                            {!forecastLoading && !forecastError && !forecast && (
                                <tr className="spot-item">
                                    <td>Статус</td>
                                    <td className="spot-item-bold">Выберите рыбу для просмотра прогноза.</td>
                                </tr>
                            )}
                            {!forecastLoading && !forecastError && !!forecast?.bestWindow && (
                                <tr className="spot-item">
                                    <td>Лучшее время</td>
                                    <td className="spot-item-bold">
                                        {forecast.bestWindow.datetime} - {forecast.bestWindow.label} ({forecast.bestWindow.scorePercent}%)
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {!!forecastTable && (
                        <div className="fish-forecast-table-wrap">
                            <table className="fish-forecast-table">
                                <tbody>
                                    <tr>
                                        <td className="fish-forecast-table__day-cell">Дата/Часы</td>
                                        {forecastTable.hours.map((hour) => (
                                            <td className="fish-forecast-table__day-cell" key={hour}>{hour}</td>
                                        ))}
                                    </tr>
                                    {forecastTable.dayKeys.map((day) => (
                                        <tr key={day}>
                                            <td className="fish-forecast-table__day-cell">
                                                {new Date(`${day}T00:00:00`).toLocaleDateString("ru-RU", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                })}
                                            </td>
                                            {forecastTable.hours.map((hour) => {
                                                const slot = forecastTable.map[`${day}_${hour}`];
                                                const percent = Number(slot?.scorePercent ?? 0);
                                                const hue = Math.max(0, Math.min(120, Math.round((percent / 100) * 120)));
                                                const bg = `linear-gradient(135deg, hsla(${hue}, 90%, 40%, 0.32), hsla(${hue}, 95%, 28%, 0.74))`;
                                                return (
                                                    <td key={`${day}_${hour}`} style={slot ? { background: bg } : undefined}>
                                                        {slot ? (
                                                            <a
                                                                href="#fish-forecast-details"
                                                                className={`fish-score-cell ${selectedForecastSlot?.datetime === slot?.datetime ? "is-selected" : ""}`}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    setSelectedForecastSlot(slot);
                                                                }}
                                                            >
                                                                {percent}%
                                                            </a>
                                                        ) : "—"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!!selectedFish && !forecastLoading && !forecastError && !!forecast && !!selectedForecastSlot && (
                        <>
                            <table className="spot-info" id="fish-forecast-details">
                                <tbody>
                                    <tr className="spot-item">
                                        <td>Выбранная дата/время</td>
                                        <td className="spot-item-bold">{selectedForecastSlot.datetime}</td>
                                    </tr>
                                    <tr className="spot-item">
                                        <td>Шанс клёва</td>
                                        <td className="spot-item-bold">{selectedForecastSlot.label} ({selectedForecastSlot.scorePercent}%)</td>
                                    </tr>
                                    <tr className="spot-item">
                                        <td>Краткое описание</td>
                                        <td className="spot-item-bold">{selectedForecastSlot.summary}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <table className="spot-info">
                                <thead>
                                    <tr className="spot-info-header">
                                        <td colSpan={2}>Факторы (выдержка)</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="spot-item spot-item--factor-summary">
                                    <td className="spot-factor-legend-cell">
                                        <div className="spot-factor-legend" aria-label="Обозначение цветов факторов">
                                            <div className="spot-factor-legend__item">
                                                <span className="spot-factor-legend__swatch spot-factor-legend__swatch--positive" aria-hidden />
                                                <span>Благоприятно</span>
                                            </div>
                                            <div className="spot-factor-legend__item">
                                                <span className="spot-factor-legend__swatch spot-factor-legend__swatch--neutral" aria-hidden />
                                                <span>Без влияния</span>
                                            </div>
                                            <div className="spot-factor-legend__item">
                                                <span className="spot-factor-legend__swatch spot-factor-legend__swatch--negative" aria-hidden />
                                                <span>Неблагоприятно</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="spot-item-bold spot-factor-tags-cell">
                                        {(selectedForecastSlot.factors || []).map((factor) => (
                                            <span key={factor.factor} className={`fish-factor fish-factor--${factor.impact}`}>
                                                {factor.label}
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                            <table className="spot-info">
                                <thead>
                                    <tr className="spot-info-header">
                                        <td colSpan={2}>Факторы</td>
                                    </tr>
                                </thead>
                                <tbody>
                                {(selectedForecastSlot.factors || []).map((factor) => (
                                    <tr className="spot-item" key={`${selectedForecastSlot.datetime}-${factor.factor}`}>
                                        <td>{factor.label}</td>
                                        <td className="spot-item-bold">{factor.explanation}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </>
            )}
            {weatherType === "weather" && (
                <div className="spot-weather-panel">
                    {weatherTimelineLoading && <p className="page-status-message">Загрузка прогноза погоды...</p>}
                    {!weatherTimelineLoading && !!weatherTimelineError && (
                        <p className="form-message">{weatherTimelineError}</p>
                    )}
                    {!weatherTimelineLoading && !weatherTimelineError && !weatherForecastTable && weatherTimelineFetchedAt > 0 && (
                        <p className="entity-popup__muted">Нет данных о погоде в данной точке.</p>
                    )}
                    {!!weatherForecastTable && (
                        <div className="fish-forecast-table-wrap spot-weather-forecast">
                            <table className="fish-forecast-table">
                                <thead>
                                    <tr>
                                        <th>День</th>
                                        {weatherForecastTable.hours.map((hour) => (
                                            <th key={`weather-hour-${hour}`}>{hour}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {weatherForecastTable.dayKeys.map((day) => (
                                        <React.Fragment key={`weather-day-${day}`}>
                                            <tr className="weather-forecast-day-start">
                                                <td className="fish-forecast-table__day-cell weather-forecast-day-cell" rowSpan={3}>
                                                    <div className="weather-forecast-day-label">
                                                        <span>{new Date(`${day}T00:00:00`).toLocaleDateString("ru-RU", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                        })}</span>
                                                        <span className="weather-forecast-day-moon" title="Фаза луны">{getMoonPhaseEmoji(day)}</span>
                                                    </div>
                                                </td>
                                                {weatherForecastTable.hours.map((hour) => {
                                                    const slot = weatherForecastTable.map[`${day}_${hour}`];
                                                    const iconKey = pickWeatherIconKey(slot || {});
                                                    return (
                                                        <td key={`temp-${day}-${hour}`}>
                                                            {slot ? (
                                                                <div className="entity-popup-weather__data spot-weather-forecast__cell">
                                                                    <img
                                                                        className="entity-popup-weather__icon"
                                                                        src={withStaticAssetVersion(`${process.env.PUBLIC_URL || ""}/weather-icons/${WEATHER_ICON_FILES[iconKey]}`)}
                                                                        alt={slot.conditionLabel || "Осадки"}
                                                                        width={20}
                                                                        height={20}
                                                                    />
                                                                    <span>{slot.temperatureAir ?? "-"}°</span>
                                                                </div>
                                                            ) : "—"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            <tr>
                                                {weatherForecastTable.hours.map((hour) => {
                                                    const slot = weatherForecastTable.map[`${day}_${hour}`];
                                                    return (
                                                        <td key={`wind-${day}-${hour}`}>
                                                            {slot ? (
                                                                <div className="entity-popup-weather__data spot-weather-forecast__cell">
                                                                    <span
                                                                        role="img"
                                                                        aria-label="Ветер"
                                                                        style={{ display: "inline-block", transform: `rotate(${getWindArrowRotation(slot.windDirection)}deg)` }}
                                                                    >
                                                                        ↑
                                                                    </span>
                                                                    <span>{formatWindDirectionShort(slot.windDirection)} {toKmh(slot.windSpeed)} км/ч</span>
                                                                </div>
                                                            ) : "—"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            <tr className="weather-forecast-day-end">
                                                {weatherForecastTable.hours.map((hour) => {
                                                    const slot = weatherForecastTable.map[`${day}_${hour}`];
                                                    return (
                                                        <td key={`pressure-${day}-${hour}`}>
                                                            {slot ? (
                                                                <div className="entity-popup-weather__data spot-weather-forecast__cell">
                                                                    <span role="img" aria-label="Давление">📈</span>
                                                                    <span>{slot.pressure ?? "-"} мм</span>
                                                                </div>
                                                            ) : "—"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderRoutesTab = () => (
        <div className={panelClassName}>
            <div className={`entity-popup__routes-layout${variant === "page" ? " spot-routes-layout" : ""}`}>
                <div className="entity-popup__routes-list">
                    <div className="entity-popup__routes-list-head">
                        <strong>Маршруты</strong>
                        {variant === "popup" && isAuthenticated && onDrawRoute && (
                            <button
                                type="button"
                                style={{ fontSize: "20px" }}
                                className="ghost-btn"
                                aria-label="Предложить маршрут"
                                title="Предложить маршрут"
                                onClick={onDrawRoute}
                            >
                                +
                            </button>
                        )}
                    </div>
                    <div className={variant === "page" ? "spot-routes-list-body" : "entity-popup__routes-list-body"}>
                        {!routes.length && <p className="page-status-message">Маршрутов пока нет.</p>}
                        {routes.map((route, index) => (
                            <button
                                key={route.id || `route-${index}`}
                                type="button"
                                className={`btn-outline ${selectedRouteIndex === index ? "is-active" : ""}`}
                                onClick={() => setSelectedRouteIndex(index)}
                            >
                                {variant === "page"
                                    ? `Маршрут ${index + 1}${route.description ? `: ${route.description}` : ""}`
                                    : `Маршрут ${index + 1}`}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="entity-popup__routes-map">
                    <SpotMiniMap
                        latitude={spot.latitude}
                        longitude={spot.longitude}
                        title={mapTitle}
                        fishingFromBoat={spot.fishingFromBoat}
                        routes={mapRoutes}
                    />
                </div>
            </div>
        </div>
    );

    const renderCommentsTab = () => {
        const commentsClassName = variant === "page"
            ? "spot-page__comments spot-page__section spot-page__panel"
            : "entity-popup__comments";

        const content = (
            <>
                {variant === "popup" && !!actionMessage && <p className="entity-popup__muted">{actionMessage}</p>}
                <h4>
                    {variant === "page"
                        ? `Отчёты (${visibleComments.length})`
                        : "Отчёты"}
                </h4>
                {isAuthenticated && !commentFormOpen && (
                    <button
                        type="button"
                        className={`toolbar-btn${variant === "popup" ? " spot-comments-add-btn" : ""}`}
                        style={{ marginTop: 8, marginBottom: variant === "page" ? 12 : 20 }}
                        onClick={() => setCommentFormOpen(true)}
                    >
                        Добавить отчёт
                    </button>
                )}
                {isAuthenticated && commentFormOpen && (
                    <form className="spot-comment-form" onSubmit={submitComment}>
                        <div className={commentGridClass}>
                            <label>Начало рыбалки (необязательно)<input className={commentInputClass("startTime")} type="datetime-local" max={maxFishingDateTime} value={commentForm.startTime} onChange={(e) => setCommentFormField("startTime", e.target.value)} />{commentFieldError(commentFieldErrors.startTime)}</label>
                            <label>Конец рыбалки (необязательно)<input className={commentInputClass("endTime")} type="datetime-local" max={maxFishingDateTime} value={commentForm.endTime} onChange={(e) => setCommentFormField("endTime", e.target.value)} />{commentFieldError(commentFieldErrors.endTime)}</label>
                            <label>Что поймали (необязательно)<input className="ui-input" value={commentForm.whatCaught} onChange={(e) => setCommentFormField("whatCaught", e.target.value)} /></label>
                            <label>Наживка (необязательно)<input className="ui-input" value={commentForm.baitUsed} onChange={(e) => setCommentFormField("baitUsed", e.target.value)} /></label>
                            <label>Прикормка (необязательно)<input className="ui-input" value={commentForm.groundbait} onChange={(e) => setCommentFormField("groundbait", e.target.value)} /></label>
                            <label>Глубина (необязательно)<input className={commentInputClass("depth")} type="number" min="0" max="100" step="0.1" value={commentForm.depth} onChange={(e) => setCommentFormField("depth", e.target.value)} />{commentFieldError(commentFieldErrors.depth)}</label>
                            <label>Тип ловли (необязательно)
                                <OptionMultiSelect
                                    options={fishingTypeSelectOptions()}
                                    value={commentForm.fishingType}
                                    onChange={(selected) => setCommentFormField("fishingType", selected)}
                                    aria-label="Тип ловли"
                                />
                            </label>
                            {variant === "page" && (
                                <>
                                    <div className="spot-comment-text-block">
                                        <label className="spot-comment-text-block__label" htmlFor="spot-comment-text">Отчёт</label>
                                        <textarea
                                            id="spot-comment-text"
                                            className={commentInputClass("comment", "ui-textarea")}
                                            rows={2}
                                            required
                                            value={commentText}
                                            onChange={(e) => {
                                                setCommentText(e.target.value);
                                                setCommentFieldErrors?.((prev) => {
                                                    if (!prev?.comment) return prev;
                                                    const { comment: _removed, ...remaining } = prev;
                                                    return remaining;
                                                });
                                            }}
                                            disabled={commentSubmitting}
                                        />
                                        {commentFieldError(commentFieldErrors.comment)}
                                    </div>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото места</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("place", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.place)}
                                    </div>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото улова</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("catch", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.catch)}
                                    </div>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото эхолота</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("depth", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.depth)}
                                    </div>
                                </>
                            )}
                            {variant === "popup" && (
                                <>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото места</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("place", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.place)}
                                    </div>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото улова</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("catch", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.catch)}
                                    </div>
                                    <div className="spot-comment-form__row">
                                        <label className="btn-outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                            <span>Фото эхолота</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept={PHOTO_UPLOAD_ACCEPT}
                                                onChange={(e) => {
                                                    handleCommentPhotoChange("depth", e.target.files);
                                                    e.target.value = "";
                                                }}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                                            До 5 файлов, JPG/PNG, до 10 МБ каждый
                                        </span>
                                        {commentFieldError(commentFileErrors.depth)}
                                    </div>
                                    <div className="spot-comment-text-block">
                                        <label className="spot-comment-text-block__label" htmlFor="spot-comment-text-popup">Отчёт</label>
                                        <textarea
                                            id="spot-comment-text-popup"
                                            className={commentInputClass("comment", "ui-textarea")}
                                            rows={2}
                                            required
                                            value={commentText}
                                            onChange={(e) => {
                                                setCommentText(e.target.value);
                                                setCommentFieldErrors?.((prev) => {
                                                    if (!prev?.comment) return prev;
                                                    const { comment: _removed, ...remaining } = prev;
                                                    return remaining;
                                                });
                                            }}
                                            disabled={commentSubmitting}
                                        />
                                        {commentFieldError(commentFieldErrors.comment)}
                                    </div>
                                </>
                            )}
                        </div>
                        <PhotoUploadPreview
                            files={commentFiles.place}
                            onRemove={(index) => setCommentFiles((v) => ({ ...v, place: v.place.filter((_, i) => i !== index) }))}
                        />
                        <PhotoUploadPreview
                            files={commentFiles.catch}
                            onRemove={(index) => setCommentFiles((v) => ({ ...v, catch: v.catch.filter((_, i) => i !== index) }))}
                        />
                        <PhotoUploadPreview
                            files={commentFiles.depth}
                            onRemove={(index) => setCommentFiles((v) => ({ ...v, depth: v.depth.filter((_, i) => i !== index) }))}
                        />
                        <div className="spot-comment-form__actions">
                            <button type="submit" className="toolbar-btn" disabled={commentSubmitting}>
                                {commentSubmitting ? "Отправка..." : "Отправить"}
                            </button>
                            <button
                                type="button"
                                className="btn-outline"
                                disabled={commentSubmitting}
                                onClick={() => {
                                    setCommentFormOpen(false);
                                    setCommentError?.("");
                                    setCommentFileErrors?.({});
                                }}
                            >
                                Отмена
                            </button>
                        </div>
                        <p className="entity-popup__muted" style={{ marginTop: 8 }}>Отчёт появится на сайте после проверки модератором.</p>
                        {!!commentError && <p className="form-message">{commentError}</p>}
                    </form>
                )}
                {!isAuthenticated && (
                    <p className="entity-popup__muted" style={{ marginTop: 8 }}>
                        <button type="button" className="inline-link" onClick={openLoginModalForComments}>
                            Войдите
                        </button>
                        , чтобы оставить отчёт.
                    </p>
                )}
                {commentsLoading && <p className="page-status-message">Загрузка отчётов...</p>}
                {!commentsLoading && !visibleComments.length && <p className="page-status-message">Отчётов пока нет.</p>}
                {!!visibleComments.length && (
                    <div className={`comments-list${variant === "page" ? " spot-page__comments-list" : ""}`}>
                        {visibleComments.map((item) => (
                            <CommentItem key={item.id} comment={item} />
                        ))}
                    </div>
                )}
            </>
        );

        if (commentsMountHidden) {
            return (
                <div
                    className={commentsClassName}
                    style={{ display: activeTab === "reports" ? "block" : "none" }}
                >
                    {content}
                </div>
            );
        }

        return (
            <section className={commentsClassName}>
                {content}
            </section>
        );
    };

    return (
        <>
            <div hidden={activeTab !== "main"}>{renderMainTab()}</div>
            <div hidden={activeTab !== "photos"}>{renderPhotosTab()}</div>
            <div hidden={activeTab !== "weather"}>{renderWeatherTab()}</div>
            {activeTab === "routes" && renderRoutesTab()}
            {showNotes && isAuthenticated && (
                <div hidden={activeTab !== "notes"}>{renderNotesTab()}</div>
            )}
            <div hidden={!commentsMountHidden && activeTab !== "reports"}>
                {renderCommentsTab()}
            </div>
            {message && <p className="form-message">{message}</p>}
        </>
    );
}
